<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import Modal from './Modal.svelte';
  import EntitySearch from './EntitySearch.svelte';
  import NewArtistForm from './NewArtistForm.svelte';
  import NewVenueForm from './NewVenueForm.svelte';
  import DatePicker from './DatePicker.svelte';
  import { apiFetch } from '../lib/auth.js';

  export let mode        = 'create';
  export let open        = false;
  export let inline      = false;
  export let review      = null;
  export let performance = null;
  // When true, the prefill summary card is suppressed (caller renders it themselves)
  export let hidePrefill = false;

  const dispatch = createEventDispatcher();

  let modalEl;
  let keyboardOffset = 0;

  function handleViewportResize() {
    if (!modalEl) return;
    const vp = window.visualViewport;
    if (!vp) return;
    const kbH = window.innerHeight - (vp.height + vp.offsetTop);
    if (kbH > 100) {
      const overlap = modalEl.getBoundingClientRect().bottom - (vp.height + vp.offsetTop);
      keyboardOffset = overlap > 0 ? overlap + 16 : 0;
    } else { keyboardOffset = 0; }
  }

  onMount(() => {
    window.visualViewport?.addEventListener('resize', handleViewportResize);
    window.visualViewport?.addEventListener('scroll', handleViewportResize);
    if (!dateIso) dateIso = todayIso();
  });
  onDestroy(() => {
    window.visualViewport?.removeEventListener('resize', handleViewportResize);
    window.visualViewport?.removeEventListener('scroll', handleViewportResize);
  });

  let artistSelected  = null;
  let venueSelected   = null;
  let dateIso         = '';
  let eventName       = '';
  let performanceId   = null;
  let lastPopulatedId = null;
  let rating          = null;
  let headline        = '';
  let body            = '';
  let isPublic        = false;
  let loading         = false;
  let saving          = false;
  let error           = '';
  let success         = false;
  let activeTab       = 'show';
  let showNewArtist   = false;
  let newArtistError  = '';
  let newArtistName   = '';
  let showNewVenue    = false;
  let newVenueError   = '';
  let newVenueName    = '';
  let confirmDelete   = false;
  let deleting        = false;

  function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }

  async function onNewArtist(e) {
    newArtistError = '';
    try {
      artistSelected = await apiFetch('/artists', { method: 'POST', body: JSON.stringify(e.detail) });
      showNewArtist = false;
    } catch (err) { newArtistError = err.message || 'Failed to create artist'; }
  }

  async function onNewVenue(e) {
    newVenueError = '';
    try {
      venueSelected = await apiFetch('/venues', { method: 'POST', body: JSON.stringify(e.detail) });
      showNewVenue = false;
    } catch (err) { newVenueError = err.message || 'Failed to create venue'; }
  }

  $: if ((open || inline) && mode === 'edit' && review?.id) populateEdit(review);
  function populateEdit(r) {
    if (lastPopulatedId === r.id) return;
    lastPopulatedId = r.id; error = '';
    rating = r.rating; headline = r.headline || ''; body = r.body || ''; isPublic = r.is_public || false;
    performanceId  = r.performance_id;
    dateIso        = r.performance_date?.slice(0, 10) || todayIso();
    artistSelected = r.artist_name ? { id: r.artist_id ?? null, name: r.artist_name } : null;
    venueSelected  = r.venue_name  ? { id: r.venue_id  ?? null, name: r.venue_name  } : null;
    eventName      = r.event_name  || '';
  }

  $: if ((open || inline) && mode === 'create' && performance) populateCreate(performance);
  function populateCreate(p) {
    rating = null; headline = ''; body = ''; isPublic = false; error = ''; success = false;
    performanceId  = p.id;
    dateIso        = p.performance_date?.slice(0, 10) || todayIso();
    artistSelected = p.artist_name ? { id: p.artist_id ?? null, name: p.artist_name } : null;
    venueSelected  = p.venue_name  ? { id: p.venue_id  ?? null, name: p.venue_name  } : null;
    eventName      = p.event_name  || '';
  }

  function resetState() {
    rating = null; headline = ''; body = ''; isPublic = false;
    error = ''; success = false; saving = false; loading = false;
    artistSelected = null; venueSelected = null;
    dateIso = todayIso(); eventName = '';
    performanceId = null; activeTab = 'show'; keyboardOffset = 0;
    showNewArtist = false; showNewVenue = false; confirmDelete = false;
    lastPopulatedId = null;
  }
  function close() { resetState(); dispatch('close'); }

  async function saveEdit() {
    error = '';
    if (!rating) { error = 'Select a rating'; return; }
    saving = true;
    try {
      await apiFetch(`/reviews/${review.id}`, {
        method: 'PUT',
        body: JSON.stringify({ rating, headline: headline.trim() || null, body: body.trim() || null, is_public: isPublic }),
      });
      await apiFetch(`/performances/${performanceId}`, {
        method: 'PUT',
        body: JSON.stringify({ artist_id: artistSelected?.id ?? undefined, venue_id: venueSelected?.id ?? null, performance_date: dateIso }),
      });
      success = true;
      setTimeout(() => { dispatch('saved'); close(); }, 1000);
    } catch (e) { error = e.message || 'Something went wrong'; }
    finally { saving = false; }
  }

  async function saveCreate() {
    error = '';
    if (!performance && !artistSelected) { error = 'Add an artist'; return; }
    if (!rating) { error = 'Select a rating'; return; }
    saving = true;
    try {
      if (performance) {
        await apiFetch('/reviews', { method: 'POST', body: JSON.stringify({ performance_id: performanceId, rating: Number(rating), headline: headline.trim() || null, body: body.trim() || null, is_public: isPublic }) });
      } else {
        let artistId = artistSelected.id;
        if (artistId === null) { const a = await apiFetch('/artists', { method: 'POST', body: JSON.stringify({ name: artistSelected.name.trim() }) }); artistId = a.id; }
        let venueId = venueSelected?.id ?? null;
        if (venueSelected && venueSelected.id === null) { const v = await apiFetch('/venues', { method: 'POST', body: JSON.stringify({ name: venueSelected.name.trim() }) }); venueId = v.id; }
        await apiFetch('/reviews/quick', { method: 'POST', body: JSON.stringify({ artist_id: artistId, performance_date: dateIso, venue_id: venueId, rating: Number(rating), headline: headline.trim() || null, body: body.trim() || null, is_public: isPublic }) });
      }
      success = true; dispatch('logged');
      setTimeout(() => { close(); }, 1200);
    } catch (e) { error = e.message || 'Something went wrong'; }
    finally { saving = false; }
  }

  function submit() { mode === 'edit' ? saveEdit() : saveCreate(); }

  async function deleteReview() {
    deleting = true;
    try {
      await apiFetch(`/reviews/${review.id}`, { method: 'DELETE' });
      dispatch('deleted'); close();
    } catch (e) { error = e.message || 'Failed to delete'; confirmDelete = false; }
    finally { deleting = false; }
  }

  $: modalTitle  = mode === 'edit' ? 'Edit Review' : 'Log Show';
  $: submitLabel = saving ? (mode === 'edit' ? 'SAVING…' : 'LOGGING…') : (mode === 'edit' ? 'SAVE CHANGES' : 'LOG IT');
</script>

{#if inline}
  <div class="form form--inline">
    {#if success}
      <div class="success">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        {mode === 'edit' ? 'SAVED' : 'LOGGED'}
      </div>
    {:else}
      {#if error}<div class="form-error">{error}</div>{/if}

      {#if performance && !hidePrefill}
        <div class="prefill-bar">
          <div class="prefill-row"><span class="prefill-label">Artist</span><span class="prefill-value">{performance.artist_name}{#if performance.artist_country} <span class="prefill-sub">({performance.artist_country})</span>{/if}</span></div>
          <div class="prefill-row"><span class="prefill-label">Date</span><span class="prefill-value">{#if performance.performance_date}{performance.performance_date.slice(8,10)}·{performance.performance_date.slice(5,7)}·{performance.performance_date.slice(0,4)}{:else}—{/if}</span></div>
          {#if performance.venue_name}<div class="prefill-row"><span class="prefill-label">Venue</span><span class="prefill-value">{performance.venue_name}{#if performance.venue_city}, {performance.venue_city}{/if}</span></div>{/if}
          {#if performance.event_name && performance.event_type !== 'quick_log'}<div class="prefill-row"><span class="prefill-label">Event</span><span class="prefill-value">{performance.event_name} <span class="event-pip">{performance.event_type}</span></span></div>{/if}
        </div>
      {:else if !performance}
        <EntitySearch label="Artist" endpoint="/artists" searchParam="name" displayField="name" subField="formed_year" placeholder={mode === 'edit' ? 'Search artist…' : 'Search or type a name…'} required bind:value={artistSelected}>
          <svelte:fragment slot="add-new" let:query>
            <div class="drop-create" role="button" tabindex="0" on:click={() => { newArtistName = query?.trim() || ''; showNewArtist = true; }} on:keydown={e => e.key === 'Enter' && (newArtistName = query?.trim() || '') && (showNewArtist = true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add "{query?.trim() || 'new artist'}"
            </div>
          </svelte:fragment>
        </EntitySearch>
        {#if showNewArtist}<NewArtistForm initialName={newArtistName} error={newArtistError} on:submit={onNewArtist} on:cancel={() => { showNewArtist = false; newArtistError = ''; newArtistName = ''; }} />{/if}

        <div class="field">


          <label>Date *</label>


          <DatePicker bind:value={dateIso} max={todayIso()} />


        </div>

        <EntitySearch label="Venue" endpoint="/venues" searchParam="name" displayField="name" subField="city" placeholder="Search venues…" bind:value={venueSelected}>
          <svelte:fragment slot="add-new" let:query>
            <div class="drop-create" role="button" tabindex="0" on:click={() => { newVenueName = query?.trim() || ''; showNewVenue = true; }} on:keydown={e => e.key === 'Enter' && (newVenueName = query?.trim() || '') && (showNewVenue = true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add "{query?.trim() || 'new venue'}"
            </div>
          </svelte:fragment>
        </EntitySearch>
        {#if showNewVenue}<NewVenueForm initialName={newVenueName} error={newVenueError} on:submit={onNewVenue} on:cancel={() => { showNewVenue = false; newVenueError = ''; newVenueName = ''; }} />{/if}

        {#if eventName}
          <div class="field"><span class="field-label">Event</span><div class="event-readonly"><span>{eventName}</span><span class="readonly-badge">curated</span></div><p class="field-hint">Part of a curated event — edit from the Events manager.</p></div>
        {/if}
      {/if}

      <div class="field">
        <span class="field-label">Rating *</span>
        <div class="rating-row">
          {#each [1,2,3,4,5,6,7,8,9,10] as n}
            <button type="button" class="rating-btn" class:active={rating === n} class:dim={rating !== null && n > rating} on:click={() => rating = (rating === n ? null : n)}>{n}</button>
          {/each}
        </div>
      </div>
      <div class="field">
        <label for="rf-il-headline">Headline <span class="hint">optional</span></label>
        <input id="rf-il-headline" type="text" bind:value={headline} placeholder="One-liner…" maxlength="60" />
        <div class="char-count" class:warn={headline.length > 50}>{headline.length}/60</div>
      </div>
      <div class="field">
        <label for="rf-il-notes">Notes <span class="hint">optional</span></label>
        <textarea id="rf-il-notes" bind:value={body} placeholder="How was it?" rows="4" maxlength="5000"></textarea>
        <div class="char-count" class:warn={body.length > 4800}>{body.length}/5000</div>
      </div>
      <div class="field toggle-field">
        <span class="field-label">Visibility</span>
        <div class="toggle-row">
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="toggle" class:on={isPublic} role="switch" aria-checked={isPublic} tabindex="0" on:click={() => isPublic = !isPublic} on:keydown={e => e.key === ' ' && (isPublic = !isPublic)}><span class="toggle-knob"></span></div>
          <span class="toggle-label">
            {#if isPublic}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>Public
            {:else}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>Private{/if}
          </span>
        </div>
        <p class="field-hint">{isPublic ? 'Visible to anyone on the site.' : 'Only visible to you.'}</p>
      </div>

      <div class="form-actions">
        {#if mode === 'edit' && confirmDelete}
          <span class="delete-confirm-text">Delete this review?</span>
          <button class="btn-delete-confirm" on:click={deleteReview} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
          <button class="btn-cancel" on:click={() => confirmDelete = false}>No</button>
        {:else}
          {#if mode === 'edit'}<button class="btn-delete" on:click={() => confirmDelete = true} disabled={saving}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>{/if}
          <button class="btn-cancel" on:click={() => dispatch('cancel')}>Cancel</button>
          <button class="btn-submit" on:click={submit} disabled={saving}>{submitLabel}</button>
        {/if}
      </div>
    {/if}
  </div>

{:else}
  <Modal title={modalTitle} open={open} on:close={close} wide={mode === 'edit'}>
    <div class="form" bind:this={modalEl} style="transform: translateY(-{keyboardOffset}px); transition: transform 0.2s ease;">
      {#if success}
        <div class="success"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{mode === 'edit' ? 'SAVED' : 'LOGGED'}</div>
      {:else if loading}
        <div class="loading-state"><span class="spinner-lg"></span><span>Loading…</span></div>
      {:else}
        {#if error}<div class="form-error">{error}</div>{/if}

        {#if mode === 'edit'}
          <div class="tabs">
            <button class="tab" class:active={activeTab === 'show'} on:click={() => activeTab = 'show'}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              The Show
            </button>
            <button class="tab" class:active={activeTab === 'review'} on:click={() => activeTab = 'review'}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Your Review
            </button>
          </div>

          {#if activeTab === 'show'}
            <div class="panel">
              <EntitySearch label="Artist" endpoint="/artists" searchParam="name" displayField="name" subField="formed_year" placeholder="Search artist…" required bind:value={artistSelected}>
                <svelte:fragment slot="add-new" let:query>
                  <div class="drop-create" role="button" tabindex="0" on:click={() => { newArtistName = query?.trim() || ''; showNewArtist = true; }} on:keydown={e => e.key === 'Enter' && (newArtistName = query?.trim() || '') && (showNewArtist = true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add "{query?.trim() || 'new artist'}"
                  </div>
                </svelte:fragment>
              </EntitySearch>
              {#if showNewArtist}<NewArtistForm initialName={newArtistName} error={newArtistError} on:submit={onNewArtist} on:cancel={() => { showNewArtist = false; newArtistError = ''; newArtistName = ''; }} />{/if}

              <div class="field">


                <label>Date *</label>


                <DatePicker bind:value={dateIso} max={todayIso()} />


              </div>

              <EntitySearch label="Venue" endpoint="/venues" searchParam="name" displayField="name" subField="city" placeholder="Search venue…" bind:value={venueSelected}>
                <svelte:fragment slot="add-new" let:query>
                  <div class="drop-create" role="button" tabindex="0" on:click={() => { newVenueName = query?.trim() || ''; showNewVenue = true; }} on:keydown={e => e.key === 'Enter' && (newVenueName = query?.trim() || '') && (showNewVenue = true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add "{query?.trim() || 'new venue'}"
                  </div>
                </svelte:fragment>
              </EntitySearch>
              {#if showNewVenue}<NewVenueForm initialName={newVenueName} error={newVenueError} on:submit={onNewVenue} on:cancel={() => { showNewVenue = false; newVenueError = ''; newVenueName = ''; }} />{/if}

              {#if eventName}
                <div class="field"><span class="field-label">Event</span><div class="event-readonly"><span>{eventName}</span><span class="readonly-badge">curated</span></div><p class="field-hint">Part of a curated event — edit from the Events manager.</p></div>
              {/if}
            </div>
          {:else}
            <div class="panel">
              <div class="field">
                <span class="field-label">Rating *</span>
                <div class="rating-row">
                  {#each [1,2,3,4,5,6,7,8,9,10] as n}
                    <button type="button" class="rating-btn" class:active={rating === n} class:dim={rating !== null && n > rating} on:click={() => rating = (rating === n ? null : n)}>{n}</button>
                  {/each}
                </div>
              </div>
              <div class="field"><label for="rf-m-headline">Headline <span class="hint">optional</span></label><input id="rf-m-headline" type="text" bind:value={headline} placeholder="One-liner…" maxlength="60" /><div class="char-count" class:warn={headline.length > 50}>{headline.length}/60</div></div>
              <div class="field"><label for="rf-m-notes">Notes <span class="hint">optional</span></label><textarea id="rf-m-notes" bind:value={body} placeholder="How was it?" rows="6" maxlength="5000"></textarea><div class="char-count" class:warn={body.length > 4800}>{body.length}/5000</div></div>
              <div class="field toggle-field">
                <span class="field-label">Visibility</span>
                <div class="toggle-row">
                  <!-- svelte-ignore a11y-click-events-have-key-events -->
                  <div class="toggle" class:on={isPublic} role="switch" aria-checked={isPublic} tabindex="0" on:click={() => isPublic = !isPublic} on:keydown={e => e.key === ' ' && (isPublic = !isPublic)}><span class="toggle-knob"></span></div>
                  <span class="toggle-label">
                    {#if isPublic}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>Public
                    {:else}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>Private{/if}
                  </span>
                </div>
                <p class="field-hint">{isPublic ? 'Visible to anyone on the site.' : 'Only visible to you.'}</p>
              </div>
            </div>
          {/if}

          {#if confirmDelete}
            <div class="delete-confirm-row"><span class="delete-confirm-text">Delete this review?</span><button class="btn-delete-confirm" on:click={deleteReview} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button><button class="btn-cancel-sm" on:click={() => confirmDelete = false}>No</button></div>
          {:else}
            <div class="delete-row"><button class="btn-delete-text" on:click={() => confirmDelete = true} disabled={saving}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>Delete review</button></div>
            <button class="btn-save" on:click={submit} disabled={saving}>{submitLabel}</button>
          {/if}

        {:else}
          <EntitySearch label="Artist" endpoint="/artists" searchParam="name" displayField="name" subField="formed_year" placeholder="Search or type a name…" required bind:value={artistSelected}>
            <svelte:fragment slot="add-new" let:query>
              <div class="drop-create" role="button" tabindex="0" on:click={() => { newArtistName = query?.trim() || ''; showNewArtist = true; }} on:keydown={e => e.key === 'Enter' && (newArtistName = query?.trim() || '') && (showNewArtist = true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add "{query?.trim() || 'new artist'}"
              </div>
            </svelte:fragment>
          </EntitySearch>
          {#if showNewArtist}<NewArtistForm initialName={newArtistName} error={newArtistError} on:submit={onNewArtist} on:cancel={() => { showNewArtist = false; newArtistError = ''; newArtistName = ''; }} />{/if}

          <div class="field">


            <label>Date *</label>


            <DatePicker bind:value={dateIso} max={todayIso()} />


          </div>

          <EntitySearch label="Venue" endpoint="/venues" searchParam="name" displayField="name" subField="city" placeholder="Search venues…" bind:value={venueSelected}>
            <svelte:fragment slot="add-new" let:query>
              <div class="drop-create" role="button" tabindex="0" on:click={() => { newVenueName = query?.trim() || ''; showNewVenue = true; }} on:keydown={e => e.key === 'Enter' && (newVenueName = query?.trim() || '') && (showNewVenue = true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add "{query?.trim() || 'new venue'}"
              </div>
            </svelte:fragment>
          </EntitySearch>
          {#if showNewVenue}<NewVenueForm initialName={newVenueName} error={newVenueError} on:submit={onNewVenue} on:cancel={() => { showNewVenue = false; newVenueError = ''; newVenueName = ''; }} />{/if}

          <div class="field">
            <span class="field-label">Rating *</span>
            <div class="rating-row">
              {#each [1,2,3,4,5,6,7,8,9,10] as n}
                <button type="button" class="rating-btn" class:active={rating === n} class:dim={rating !== null && n > rating} on:click={() => rating = (rating === n ? null : n)}>{n}</button>
              {/each}
            </div>
          </div>
          <div class="field"><label for="rf-c-headline">Headline <span class="hint">optional</span></label><input id="rf-c-headline" type="text" bind:value={headline} placeholder="One-liner…" maxlength="60" /><div class="char-count" class:warn={headline.length > 50}>{headline.length}/60</div></div>
          <div class="field"><label for="rf-c-notes">Notes <span class="hint">optional</span></label><textarea id="rf-c-notes" bind:value={body} placeholder="How was it?" rows="5" maxlength="5000"></textarea><div class="char-count" class:warn={body.length > 4800}>{body.length}/5000</div></div>
          <div class="field toggle-field">
            <span class="field-label">Visibility</span>
            <div class="toggle-row">
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div class="toggle" class:on={isPublic} role="switch" aria-checked={isPublic} tabindex="0" on:click={() => isPublic = !isPublic} on:keydown={e => e.key === ' ' && (isPublic = !isPublic)}><span class="toggle-knob"></span></div>
              <span class="toggle-label">
                {#if isPublic}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>Public
                {:else}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>Private{/if}
              </span>
            </div>
            <p class="field-hint">{isPublic ? 'Visible to anyone on the site.' : 'Only visible to you.'}</p>
          </div>
          <button class="btn-save" on:click={submit} disabled={saving}>{submitLabel}</button>
        {/if}
      {/if}
    </div>
  </Modal>
{/if}

<style>
  .form { display: flex; flex-direction: column; gap: 0; }
  .form--inline { padding: 1rem; background: #040a00; border-top: 1px solid #1a3300; animation: slideIn 0.15s ease; }
  @keyframes slideIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

  .prefill-bar { display: flex; flex-direction: column; gap: 0.4rem; background: #060e00; border: 1px solid #1e3d00; border-radius: 2px; padding: 0.65rem 0.85rem; margin-bottom: 1.1rem; }
  .prefill-row { display: flex; align-items: baseline; gap: 0.6rem; }
  .prefill-label { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #2a5200; width: 3rem; flex-shrink: 0; }
  .prefill-value { font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; font-weight: 600; color: #7a9a50; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .prefill-sub { color: #3a6b00; font-size: 0.75rem; }
  .event-pip { font-size: 0.48rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2a5200; border: 1px solid #1a3300; border-radius: 2px; padding: 0.05rem 0.3rem; }

  .loading-state { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 3rem; font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00; }
  .spinner-lg { width: 20px; height: 20px; border: 2px solid #1e3d00; border-top-color: #6db800; border-radius: 50%; animation: spin 0.7s linear infinite; flex-shrink: 0; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .tabs { display: flex; margin-bottom: 1.5rem; border-bottom: 1px solid #1e3d00; }
  .tab { display: flex; align-items: center; gap: 0.4rem; background: none; border: none; border-bottom: 2px solid transparent; color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; padding: 0.6rem 1rem 0.7rem; cursor: pointer; margin-bottom: -1px; transition: color 0.15s, border-color 0.15s; }
  .tab svg { width: 13px; height: 13px; flex-shrink: 0; }
  .tab:hover { color: #3a6b00; }
  .tab.active { color: #6db800; border-bottom-color: #6db800; }
  .panel { min-height: 260px; }

  .field { margin-bottom: 1.1rem; }


  .event-readonly { display: flex; align-items: center; justify-content: space-between; background: #080808; border: 1px solid #141414; border-radius: 2px; padding: 0.6rem 0.75rem; font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 1rem; color: #555; }
  .readonly-badge { font-size: 0.5rem; letter-spacing: 0.15em; text-transform: uppercase; background: #0f0f0f; border: 1px solid #222; border-radius: 2px; color: #333; padding: 0.1rem 0.4rem; }

  .rating-row { display: flex; gap: 0.3rem; flex-wrap: wrap; }
  .rating-btn { width: 2.4rem; height: 2.4rem; background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px; color: #3a6b00; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.9rem; cursor: pointer; transition: all 0.12s; }
  .rating-btn:hover { background: #0d1a00; border-color: #3a6b00; color: #6db800; }
  .rating-btn.active { background: #1a3300; border-color: #6db800; color: #6db800; box-shadow: 0 0 8px rgba(109,184,0,0.4); }
  .rating-btn.dim { opacity: 0.3; }

  .toggle-field { gap: 0.6rem; }
  .toggle-row { display: flex; align-items: center; gap: 0.75rem; }
  .toggle { width: 2.4rem; height: 1.25rem; flex-shrink: 0; background: #0f0f0f; border: 1px solid #1e3d00; border-radius: 999px; position: relative; cursor: pointer; transition: background 0.2s, border-color 0.2s; }
  .toggle.on { background: #1a3300; border-color: #3a6b00; }
  .toggle-knob { position: absolute; top: 2px; left: 2px; width: calc(1.25rem - 8px); height: calc(1.25rem - 8px); background: #2a5200; border-radius: 50%; transition: transform 0.2s, background 0.2s; }
  .toggle.on .toggle-knob { transform: translateX(1.15rem); background: #6db800; }
  .toggle-label { display: flex; align-items: center; gap: 0.35rem; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.75rem; letter-spacing: 0.15em; text-transform: uppercase; color: #c8c8c8; }
  .toggle-label svg { width: 13px; height: 13px; }

  :global(.drop-create) { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 0.75rem; cursor: pointer; font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.05em; color: #3a6b00; border-top: 1px solid #1e3d00; transition: color 0.12s, background 0.12s; }
  :global(.drop-create:hover) { color: #6db800; background: #0d1a00; }
  :global(.drop-create svg) { width: 13px; height: 13px; flex-shrink: 0; }

  .btn-save { width: 100%; margin-top: 0.5rem; background: #1a3300; border: 1px solid #3a6b00; color: #6db800; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem; letter-spacing: 0.3em; text-transform: uppercase; padding: 0.85rem; cursor: pointer; border-radius: 2px; transition: background 0.2s, box-shadow 0.2s; }
  .btn-save:hover { background: #223d00; box-shadow: 0 0 14px rgba(109,184,0,0.35); }
  .btn-save:disabled { opacity: 0.4; cursor: not-allowed; }
  .form-actions { display: flex; gap: 0.5rem; margin-top: 0.25rem; }
  .btn-cancel { flex: 0 0 auto; background: none; border: 1px solid #1e3d00; border-radius: 2px; color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem; letter-spacing: 0.22em; text-transform: uppercase; padding: 0.75rem 1rem; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s; }
  .btn-cancel:hover { color: #cc4400; border-color: #4a1000; background: rgba(204,68,0,0.06); }
  .btn-cancel-sm { background: none; border: 1px solid #1e3d00; border-radius: 2px; color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; padding: 0.5rem 0.75rem; cursor: pointer; transition: color 0.15s, border-color 0.15s; }
  .btn-cancel-sm:hover { color: #cc4400; border-color: #4a1000; }
  .btn-submit { flex: 1; background: #1a3300; border: 1px solid #3a6b00; color: #6db800; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem; letter-spacing: 0.28em; text-transform: uppercase; padding: 0.75rem; cursor: pointer; border-radius: 2px; transition: background 0.2s, box-shadow 0.2s; }
  .btn-submit:hover { background: #223d00; box-shadow: 0 0 12px rgba(109,184,0,0.3); }
  .btn-submit:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-delete { flex: 0 0 auto; background: none; border: 1px solid #2a1000; border-radius: 2px; color: #4a1500; padding: 0.75rem; cursor: pointer; display: inline-flex; align-items: center; transition: color 0.15s, border-color 0.15s, background 0.15s; }
  .btn-delete svg { width: 13px; height: 13px; }
  .btn-delete:hover { color: #cc4400; border-color: #4a1000; background: rgba(204,68,0,0.08); }
  .btn-delete:disabled { opacity: 0.3; cursor: not-allowed; }
  .delete-row { margin-top: 0.5rem; }
  .btn-delete-text { display: inline-flex; align-items: center; gap: 0.4rem; background: none; border: none; color: #3a1500; font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; padding: 0.4rem 0; cursor: pointer; transition: color 0.15s; }
  .btn-delete-text svg { width: 12px; height: 12px; }
  .btn-delete-text:hover { color: #cc4400; }
  .btn-delete-text:disabled { opacity: 0.3; cursor: not-allowed; }
  .delete-confirm-row { display: flex; align-items: center; gap: 0.6rem; margin-top: 0.5rem; margin-bottom: 0.25rem; }
  .delete-confirm-text { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #cc4400; flex: 1; }
  .btn-delete-confirm { background: rgba(204,68,0,0.15); border: 1px solid #cc4400; border-radius: 2px; color: #cc4400; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; padding: 0.5rem 0.85rem; cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
  .btn-delete-confirm:hover { background: rgba(204,68,0,0.25); box-shadow: 0 0 8px rgba(204,68,0,0.3); }

  .form-error { background: rgba(204,68,0,0.1); border: 1px solid rgba(204,68,0,0.3); border-radius: 2px; color: #cc6633; font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; letter-spacing: 0.1em; padding: 0.6rem 0.75rem; margin-bottom: 1rem; text-transform: uppercase; }
  .success { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 2.5rem; color: #6db800; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.2rem; letter-spacing: 0.4em; text-shadow: 0 0 12px rgba(109,184,0,0.6); }
  .success svg { width: 28px; height: 28px; stroke: #6db800; }
</style>
